import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider, ThemeSelector } from './components/Theme';
import { OfflineIndicator } from './components/PWA';
import { UpdateNotification } from './components/UpdateNotification';
import { ErrorBoundary } from './components/Common';
import { AppLayout } from './components/Layout';
import { HomePage } from './pages';

// Lazy load heavy components to reduce initial bundle size
const NotesPage = lazy(() => import('./pages/NotesPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
import { AuthModal } from './components/Auth';
import { useAuthStore } from './store/authStore';
import useNoteStore from './store/noteStore';
import { scheduleReminderCheck } from './services/notificationService';
import { generateDailySummary, formatSummaryText, shouldSendDailySummary } from './services/dailySummaryService';
import { showNotification } from './services/notificationService';
import './styles/globals.css';

function App() {
  const { setToken, fetchProfile } = useAuthStore();
  const { notes, updateNote } = useNoteStore();

  // Handle OAuth callback - capture token from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const error = params.get('error');

    if (token) {
      // Store token and fetch user profile
      setToken(token);
      fetchProfile();
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (error) {
      console.error('OAuth error:', error);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [setToken, fetchProfile]);

  // Schedule notification checks on app load
  useEffect(() => {
    // Check reminders
    scheduleReminderCheck(notes, (noteId, reminderId) => {
      // Mark reminder as notified
      const note = notes.find(n => n.id === noteId || n.localId === noteId);
      if (note?.reminders) {
        const updatedReminders = note.reminders.map(r =>
          r.id === reminderId ? { ...r, notified: true } : r
        );
        updateNote(noteId, { reminders: updatedReminders });
      }
    });

    // Check daily summary
    const lastSent = localStorage.getItem('lastDailySummary');
    if (shouldSendDailySummary(lastSent)) {
      const summary = generateDailySummary(notes);
      if (summary.totalToday > 0 || summary.totalUpcoming > 0) {
        showNotification('📅 Tóm tắt hôm nay', {
          body: formatSummaryText(summary),
          tag: 'daily-summary',
        });
        localStorage.setItem('lastDailySummary', new Date().toISOString().split('T')[0]);
      }
    }
  }, [notes, updateNote]);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <OfflineIndicator />
          <UpdateNotification />
          <Suspense fallback={
            <div className="flex items-center justify-center min-h-screen">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          }>
            <Routes>
              <Route element={<AppLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/notes" element={<NotesPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/account" element={<AccountPage />} />
              </Route>
            </Routes>
          </Suspense>
          <ThemeSelector />
          <AuthModal />
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;

// Cache bust 1769702107
